/** Project report registry: the list the Reports tab, its URL and the access gates read from. A new report is one entry here plus a component in reports/. */

import type { ProjectPermissions } from '../../projects/hooks/projectPermissionsModel';
import type { ProjectReportAccess } from './projectReportTypes';

export type ProjectReportId =
  | 'summary' | 'valuations' | 'variations' | 'rfis' | 'programme' | 'site-diary' | 'health-safety' | 'documents';

export interface ProjectReportDefinition {
  id: ProjectReportId;
  title: string;
  description: string;
  /** True when the period filter changes what the report shows. */
  dateScoped: boolean;
  /** Wide tables print landscape. */
  wide: boolean;
  allowed: (access: ProjectReportAccess) => boolean;
}

type Permissions = Partial<ProjectPermissions> | null;
const can = (permissions: Permissions, key: keyof ProjectPermissions) => permissions?.[key] === true;

/** Mirrors the Valuations tab: older permission records fall back to the financial grant. */
export const canSeeApplications = (permissions: Permissions) =>
  permissions?.canViewApplications !== undefined ? permissions.canViewApplications === true : can(permissions, 'canViewFinancial');

export const PROJECT_REPORTS: readonly ProjectReportDefinition[] = [
  {
    id: 'summary', title: 'Project summary', dateScoped: false, wide: false, allowed: () => true,
    description: 'Stage, key dates, contract sum, payments, retention and what is open, on one page.',
  },
  {
    id: 'valuations', title: 'Valuations and payments', dateScoped: true, wide: true,
    allowed: ({ permissions }) => canSeeApplications(permissions),
    description: 'Each application with its dates, what was certified and what was paid.',
  },
  {
    id: 'variations', title: 'Variations and changes', dateScoped: true, wide: true,
    allowed: ({ permissions }) => can(permissions, 'canViewItems'),
    description: 'Variations, CVIs and extensions of time, with value submitted against agreed.',
  },
  {
    id: 'rfis', title: 'RFIs and contract items', dateScoped: true, wide: true,
    allowed: ({ permissions }) => can(permissions, 'canViewItems'),
    description: 'Open and overdue requests for information and how quickly they are answered.',
  },
  {
    id: 'programme', title: 'Programme', dateScoped: false, wide: true,
    allowed: ({ permissions }) => can(permissions, 'canViewTasks'),
    description: 'Late items, milestones, progress and the forecast against the contract completion date.',
  },
  {
    id: 'site-diary', title: 'Site diary and labour', dateScoped: true, wide: true,
    allowed: ({ siteRegister }) => siteRegister,
    description: 'Diary entries, labour on site, visitors, deliveries and delays.',
  },
  {
    id: 'health-safety', title: 'Health and safety', dateScoped: true, wide: true,
    allowed: () => true,
    description: 'Near misses, toolbox talks, RAMS review dates and plant check compliance.',
  },
  {
    id: 'documents', title: 'Document register', dateScoped: true, wide: true,
    allowed: ({ permissions }) => can(permissions, 'canViewDocuments'),
    description: 'Documents with their current revision and status, and what was issued in the period.',
  },
];

export function visibleProjectReports(access: ProjectReportAccess): ProjectReportDefinition[] {
  return PROJECT_REPORTS.filter((report) => report.allowed(access));
}

const PREFIX = 'reports/';

/** The project tab id for a report; bare `reports` is the rail entry and shows the report cards. */
export const projectReportTab = (id: ProjectReportId) => `${PREFIX}${id}`;

export function isProjectReportsTab(tab: string | null | undefined): boolean {
  return tab === 'reports' || !!tab?.startsWith(PREFIX);
}

export function projectReportIdFromTab(tab: string | null | undefined): ProjectReportId | null {
  const id = tab?.startsWith(PREFIX) ? tab.slice(PREFIX.length) : null;
  return PROJECT_REPORTS.find((report) => report.id === id)?.id ?? null;
}

/** The report the address names, if the user may see it. Bare `reports` names none: it shows the cards. */
export function resolveProjectReport(tab: string | null | undefined, access: ProjectReportAccess): ProjectReportDefinition | null {
  const wanted = projectReportIdFromTab(tab);
  return visibleProjectReports(access).find((report) => report.id === wanted) ?? null;
}
